/**
 * @file SessionTagsMenu.tsx
 * @brief An interpretation session's tags as a dropdown under its button
 *        (TagsMenu), saved on each change.
 *
 * @details
 * DES 04-10-2026: "yehi kaam sessions per bhi karna hai" — the meeting's tag
 * menu, for sessions: the Home card's # button and the session page's "Add
 * tags" chip. Saves through `PATCH /api/sessions/:id/tags` with
 * `append: false` (what the phone sends); the route checks
 * `verifySessionAccess`, so the owner, participant B and their company admin
 * may tag. "Your tags" come from the sessions list's `available_tags`.
 * After a save every cached copy of the session refetches: Home's rail, the
 * admin and company lists, and the session page.
 *
 * @author DES
 * @date 04-10-2026
 */

import { useState, type ReactElement } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiClientError, apiPatch } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { useSessionTagCatalogue } from "@/features/home/homeApi";
import { TagsMenu } from "@/features/notes/TagsMenu";

export function SessionTagsMenu({
  sessionId,
  tags,
  anchor,
  align = "start",
  side,
  sideOffset,
  open: openProp,
  onOpenChange,
}: {
  sessionId: string;
  tags: string[];
  anchor: ReactElement;
  /** Controlled open: a dock keeps one menu open at a time (DES 04-10-2026). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: "start" | "center" | "end";
  side?: "top" | "bottom";
  sideOffset?: number;
}) {
  const qc = useQueryClient();
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  // The menu shows this draft so a tap reads at once; it resets from the
  // session each time the menu opens.
  const [draft, setDraft] = useState<string[]>(tags);
  const catalogue = useSessionTagCatalogue(open);

  const save = useMutation({
    mutationFn: (next: string[]) =>
      apiPatch<{ success?: boolean; tags?: string[] }>(
        `/api/sessions/${encodeURIComponent(sessionId)}/tags`,
        { tags: next, append: false },
      ),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["home", "sessions"] });
      void qc.invalidateQueries({ queryKey: ["admin", "sessions"] });
      void qc.invalidateQueries({ queryKey: ["company", "sessions"] });
    },
  });

  return (
    <TagsMenu
      anchor={anchor}
      align={align}
      side={side}
      sideOffset={sideOffset}
      tags={draft}
      suggestions={(catalogue.data ?? []).map((tag) => ({ tag, uses: 0 }))}
      saving={save.isPending}
      open={openProp}
      onOpenChange={(o) => {
        setOpenState(o);
        onOpenChange?.(o);
        if (o) setDraft(tags.filter(Boolean));
      }}
      onChange={(next) => {
        const prev = draft;
        setDraft(next);
        save.mutate(next, {
          onError: (err) => {
            setDraft(prev);
            toast({
              variant: "destructive",
              title: "Could not save tags",
              description: err instanceof ApiClientError ? err.message : "Please try again.",
            });
          },
        });
      }}
    />
  );
}
